<template>
  <div class="row">
    <div class="col-xs-12">
      <p class="upper-label m-t-md m-b-none">
        <i class="fa-solid fa-arrows-rotate"></i> Recurring Subscription
      </p>
      <h1 class="font-heavy text-18 m-t-xs m-b-none">
        {{ title }}
      </h1>
      <h2 class="font-semi text-16 m-t-xxs" v-html="subtitle"></h2>
      <div class="flex flex-wrap items-center m-t-m">
        <p class="m-r-xs" v-html="statusBadge"></p>
        <p class="" v-html="collectionBadge" v-if="collectionPaused"></p>
        <p class="" v-html="scheduledPauseBadge" v-if="pauseScheduled"></p>
        <div
          class=""
          v-if="
            collectionPaused && subscription.days_remaining_when_resumed > 0
          "
        >
          <p>
            <span class="label label-warning-light"
              >Days left on contract
              {{ subscription.days_remaining_when_resumed }}</span
            >
          </p>
          <p class="m-t-none text-12">
            When you resume payments, the expiration date will be changed to
            ensure they receive their full, contracted subscription length.
          </p>
        </div>
      </div>
      <hr />
      <h3 class="m-b-sm font-heavy">Details</h3>
      <dl class="grid grid-cols-1 gap-4">
        <div
          v-if="hasCoupon"
          v-for="coupon in subscription.coupons"
          :key="coupon.id"
          class="flex justify-space-between"
        >
          <dt class="text-14 text-gray-600">Discount</dt>
          <dd>
            <span class="label label-plain bg-purple-600 text-purple-50">
              {{ coupon.code }}
            </span>
          </dd>
        </div>
        <div v-if="!hasCoupon" class="flex justify-space-between">
          <dt class="text-14 text-gray-600">Discount</dt>
          <dd>None</dd>
        </div>
        <div class="flex justify-space-between">
          <dt class="text-14">Billed to</dt>
          <dd v-html="parentName()"></dd>
        </div>
        <div
          v-if="subscriptionClassPassLabel()"
          class="flex justify-space-between"
        >
          <dt class="text-14">Class limit</dt>
          <dd>
            <i class="fa-solid fa-ticket"></i>
            {{ subscriptionClassPassLabel() }}
          </dd>
        </div>
        <div class="flex justify-space-between">
          <dt class="text-14">Created</dt>
          <dd>{{ readableCreatedAt() }}</dd>
        </div>
        <div class="flex justify-space-between">
          <dt class="text-14 flex align-items-center">
            {{ endAtVerb }}
            <span v-if="endAtVerb === 'Next due'">
              <VDropdown theme="info-tooltip">
                <!-- This will be the popover target (for the events and position) -->
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-6 h-6 text-blue-600"
                  style="margin-bottom: -2px; margin-left: 2px"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                  />
                </svg>
                <!-- This will be the content of the popover -->
                <template v-slot:popper>
                  <div>
                    <p class="header">
                      When does WrestlingIQ actually charge the card?
                    </p>
                    <p>
                      Our subscription biling system usually runs once an hour,
                      around the half hour mark. So if a subscription is due on
                      9:31am, it will be billed at 10:30am.
                    </p>
                  </div>
                </template>
              </VDropdown>
            </span>
          </dt>
          <dd>{{ readableEndAt() }}</dd>
        </div>
        <div v-if="subscription.expires_on" class="flex justify-space-between">
          <dt class="text-14">Expires on</dt>
          <dd>{{ expiresOn() }}</dd>
        </div>
        <div v-if="subscription.expires_on" class="flex justify-space-between">
          <dt class="text-14">
            # of Renewals
            <VDropdown theme="info-tooltip">
              <!-- This will be the popover target (for the events and position) -->
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-6 h-6 text-blue-600"
                style="margin-bottom: -2px; margin-left: 2px"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                />
              </svg>
              <!-- This will be the content of the popover -->
              <template v-slot:popper>
                <div>
                  <p class="header">How do renewals work?</p>
                  <p>
                    WrestlingIQ sends an automated email sequence to the
                    customer 14 days, 5 days and 1 day before expiration. The
                    email includes a 1-click option to renew their expiring
                    subscription—<a
                      href="https://help.wrestlingiq.com/article/187-how-do-expirations-and-renewals-work-with-expiring-memberships"
                      target="_blank"
                      >you can see screenshots and read more here.</a
                    >
                  </p>
                </div>
              </template>
            </VDropdown>
          </dt>
          <dd>{{ timesRenewed }}</dd>
        </div>
      </dl>
      <hr />
      <div>
        <h3 class="m-b-sm font-heavy">Billing Partner ID</h3>
        <p v-if="subscription.billing_partner === 'stripe'">
          <a
            :href="`https://dashboard.stripe.com/subscriptions/${subscription.billing_partner_id}`"
            target="_blank"
          >
            {{ subscription.billing_partner_id }}
          </a>
        </p>
        <p v-else>
          {{ subscription.billing_partner_id }}
        </p>
      </div>
      <template v-if="allowPause || allowSelfPause">
        <hr />
        <h3 class="m-b-sm font-heavy">Pauses</h3>
        <dl class="grid grid-cols-1 gap-4">
          <div class="flex justify-space-between gap-x-sm">
            <dt class="text-14 text-gray-600 flex-shrink-0">Last pause</dt>
            <dd class="text-right">{{ lastPauseSummary }}</dd>
          </div>
          <div class="flex justify-space-between gap-x-sm">
            <dt class="text-14 text-gray-600 flex-shrink-0">Next self pause</dt>
            <dd class="text-right">{{ nextPauseSummary }}</dd>
          </div>
        </dl>
      </template>
      <hr />
      <div>
        <h3 class="m-b-sm font-heavy">Edit</h3>
        <div
          class="flex flex-col gap-y-xs"
          v-if="
            paidOrOverdue &&
            !canceling &&
            !showResumeConfirm &&
            !showCancelScheduledPauseConfirm &&
            !showChangeCoupon &&
            !showRemoveCoupon &&
            !showChangePlan &&
            !showChangeTier &&
            !showDateChange
          "
        >
          <a
            class="btn btn-default flex gap-x-xs align-items-center justify-center"
            @click.prevent="showRemoveCoupon = true"
            v-if="allowCouponChange && !legacyUi && hasCoupon"
          >
            <i class="fa-solid fa-xmark"></i>
            <span> Remove discount </span>
          </a>
          <a
            class="btn btn-default flex gap-x-xs align-items-center justify-center"
            @click.prevent="showChangeCoupon = true"
            v-if="allowCouponChange && !legacyUi"
          >
            <i class="fa-solid fa-pencil"></i>
            <span>Change discount</span>
          </a>
          <a
            class="btn btn-default flex gap-x-xs align-items-center justify-center"
            @click.prevent="showChangePlan = true"
            v-if="allowPlanChange && !legacyUi"
          >
            <i class="fa-solid fa-cart-shopping"></i>
            <span>Change subscription plan</span>
          </a>
          <a
            class="btn btn-default flex gap-x-xs align-items-center justify-center"
            @click.prevent="openChangeTier"
            v-if="allowExpiringTierChange && !legacyUi"
          >
            <i class="fa-solid fa-right-left"></i>
            <span>Change subscription plan</span>
          </a>
          <template v-if="canRenew">
            <a
              class="btn btn-primary flex gap-x-xs align-items-center justify-center"
              :href="renewalUrl"
            >
              <i class="fa-solid fa-arrow-rotate-right"></i>
              <span>Renew</span>
            </a>
          </template>
          <template v-else-if="renewalOpensAt">
            <div>
              <button
                type="button"
                class="btn btn-primary btn-block flex gap-x-xs align-items-center justify-center"
                disabled
              >
                <i class="fa-solid fa-arrow-rotate-right"></i>
                <span>Renew</span>
              </button>
              <p class="text-14 text-gray-600 m-t-xxs">
                Renewal opens {{ renewalOpensAtLabel }}, once less than one term
                is left on the contract.
              </p>
            </div>
          </template>
          <template v-if="allowDateChange">
            <a
              class="btn btn-success flex gap-x-xs align-items-center justify-center"
              @click.prevent="showDateChange = true"
            >
              <i class="fa-solid fa-calendar-days"></i>
              <span>Change bill date</span>
            </a>
          </template>
          <template v-if="allowPause || allowSelfPause">
            <a
              class="btn btn-warning flex gap-x-xs align-items-center justify-center"
              v-if="collectionPaused"
              @click.prevent="showResumeConfirm = true"
            >
              <i class="fa-solid fa-play"></i>
              <span>Resume payments</span>
            </a>
            <a
              class="btn btn-warning flex gap-x-xs align-items-center justify-center"
              v-if="pauseScheduled"
              @click.prevent="showCancelScheduledPauseConfirm = true"
            >
              <i class="fa-solid fa-xmark"></i>
              <span>Cancel scheduled pause</span>
            </a>
            <a
              class="btn btn-warning flex gap-x-xs align-items-center justify-center"
              v-if="!collectionPaused && !pauseScheduled"
              @click.prevent="showPauseConfirm = true"
            >
              <i class="fa-solid fa-pause"></i>
              <span>Pause subscription</span>
            </a>
          </template>
          <template v-else>
            <a
              class="btn btn-warning flex gap-x-xs align-items-center justify-center"
              v-if="collectionPaused"
              @click.prevent="showPausePopup"
            >
              <i class="fa-solid fa-play"></i>
              <span>Resume subscription</span>
            </a>
            <a
              class="btn btn-warning flex gap-x-xs align-items-center justify-center"
              v-if="!collectionPaused"
              @click.prevent="showPausePopup"
            >
              <i class="fa-solid fa-pause"></i>
              <span>Pause subscription</span>
            </a>
          </template>
          <a
            class="btn btn-danger flex gap-x-xs align-items-center justify-center"
            @click.prevent="confirmCancel()"
          >
            <i class="fa-solid fa-xmark"></i>
            <span>Cancel subscription</span>
          </a>
        </div>
        <div
          v-if="showDateChange"
          class="archive-form should-dim modify-form m-t-sm card-bordered-dashed"
        >
          <p class="text-16 font-semi">Change billing date</p>
          <p class="text-sm">
            <strong
              >Note: Changing the next billing date also changes future billing
              dates.</strong
            >
            E.g. If you have a monthly subscription and you change the next bill
            date to the 3rd, all future bills will happen on the 3rd of the
            month.
          </p>
          <div class="form-group">
            <label for="next_bill_date">Change next bill date to:</label>
            <input
              class="form-control"
              v-model="nextBillDate"
              id="next_bill_date"
              type="datetime-local"
              name="next_bill_date"
            />
          </div>
          <div class="">
            <button v-on:click="showDateChange = false" class="btn btn-default">
              Cancel
            </button>
            <ladda-button
              @lbClicked="changeBillDate"
              el-class="btn-warning btn"
              :loading="changingBillDate"
            >
              Change bill date
            </ladda-button>
          </div>
        </div>
        <div
          v-if="showCancelScheduledPauseConfirm"
          class="archive-form should-dim modify-form m-t-sm card-bordered-dashed"
        >
          <p class="text-16 font-semi">Cancel scheduled pause</p>
          <p class="text-sm">
            Payments are scheduled to pause on {{ formattedPausesAt
            }}<span v-if="subscription.payment_collection_resumes_at">
              and resume on {{ formattedResumesAt }}</span
            >. Canceling the scheduled pause keeps payment collection active as
            normal.
          </p>
          <div class="">
            <button
              v-on:click="showCancelScheduledPauseConfirm = false"
              class="btn btn-default"
            >
              Never mind
            </button>
            <ladda-button
              @lbClicked="cancelScheduledPause"
              el-class="btn-warning btn"
              :loading="cancelingScheduledPause"
            >
              Cancel scheduled pause
            </ladda-button>
          </div>
        </div>
        <div
          v-if="showResumeConfirm"
          class="archive-form should-dim modify-form m-t-sm card-bordered-dashed"
        >
          <p class="text-16 font-semi">Resume payment collection</p>
          <p class="text-sm">
            Are you sure you want to resume payment collection? Payments will be
            collected automatically on their next bill date.<br /><br />
            <span v-if="subscription.payment_collection_resumes_at">
              Resuming now will override the previously set resume date of
              {{ formattedResumesAt }}.
            </span>
          </p>
          <div class="">
            <button
              v-on:click="showResumeConfirm = false"
              class="btn btn-default"
            >
              Cancel
            </button>
            <ladda-button
              @lbClicked="resumePayments"
              el-class="btn-primary btn"
              :loading="resuming"
            >
              Resume
            </ladda-button>
          </div>
        </div>
        <div
          v-if="showChangeCoupon"
          class="archive-form should-dim modify-form m-t-sm card-bordered-dashed"
        >
          <p class="text-16 font-semi">Change discount</p>
          <p>
            <strong
              >Only 1 scholarship can be applied to a recurring subscription at
              a time.</strong
            >
            If a scholarship code is already set on this subscription, it will
            be replaced.
          </p>
          <p class="text-sm"></p>
          <spinner v-if="couponsLoading"></spinner>
          <div v-if="!couponsLoading">
            <label
              >Which scholarship code do you want to apply to this
              subscription?</label
            >
            <select
              v-model="couponToChangeTo"
              class="form-control"
              name="scholarship_code"
            >
              <option v-for="c in availableCoupons" :key="c.id" :value="c.id">
                {{ c.code }}
                <template v-if="c.amount_off > 0">
                  ({{ formatAsCurrency(c.amount_off) }})
                </template>
                <template v-else> ({{ c.percent_off * 100 }}% off) </template>
              </option>
            </select>
          </div>
          <div class="m-t-m">
            <button
              v-on:click="showChangeCoupon = false"
              class="btn btn-default"
            >
              Cancel
            </button>
            <ladda-button
              @lbClicked="changeCoupon"
              el-class="btn-primary btn"
              :loading="changingCoupon"
            >
              Change
            </ladda-button>
          </div>
        </div>
        <div
          v-if="showRemoveCoupon"
          class="archive-form should-dim modify-form m-t-sm card-bordered-dashed"
        >
          <p class="text-16 font-semi">Remove discount</p>
          <p>
            Do you want to remove
            <strong>{{ subscription.coupons[0].code }}</strong> from this
            subscription?
          </p>
          <p class="text-sm"></p>
          <div class="m-t-m">
            <button
              v-on:click="showRemoveCoupon = false"
              class="btn btn-default"
            >
              Cancel
            </button>
            <ladda-button
              @lbClicked="removeCoupon"
              el-class="btn-danger btn"
              :loading="changingCoupon"
            >
              Remove
            </ladda-button>
          </div>
        </div>
        <div
          v-if="showChangePlan"
          class="archive-form should-dim modify-form m-t-sm card-bordered-dashed"
        >
          <p class="text-16 font-semi">Change subscription plan</p>
          <p>
            <strong>Price changes only impact future invoices.</strong> The
            {{ isDonation ? "donor" : "subscriber" }} will be charged the new
            rate on their normal bill date.
          </p>
          <p v-if="isDonation" class="text-sm">
            Donors can move between the monthly amounts on your donation page.
            Fees are recalculated and fee coverage stays as the donor originally
            chose.
          </p>
          <p class="text-sm"></p>
          <spinner v-if="loadingPlans"></spinner>
          <p v-if="!loadingPlans && isEmptyPlans" class="text-danger">
            No other plans are available for this subscription.
          </p>
          <div v-if="!loadingPlans && !isEmptyPlans">
            <label
              >Which plan do you want to move this
              {{ isDonation ? "donor" : "subscriber" }} to?</label
            >
            <select v-model="planToChangeTo" class="form-control m-b-m">
              <optgroup
                v-for="(product, name) in availablePlans"
                :key="name"
                :label="name"
                v-if="product.length > 0"
              >
                <option
                  v-for="plan in product"
                  :key="plan.id"
                  v-bind:value="plan.id"
                  :disabled="plan.id === membershipBillingPlan.id"
                >
                  {{ plan.name }}
                  <span v-if="plan.id === membershipBillingPlan.id"
                    >(current plan)</span
                  >
                </option>
              </optgroup>
            </select>
          </div>
          <div class="m-t-m">
            <button v-on:click="showChangePlan = false" class="btn btn-default">
              Cancel
            </button>
            <ladda-button
              @lbClicked="changePlan"
              el-class="btn-primary btn"
              :loading="changingPlan"
            >
              Change
            </ladda-button>
          </div>
        </div>
        <div
          v-if="showChangeTier"
          class="archive-form should-dim modify-form m-t-sm card-bordered-dashed"
        >
          <p class="text-16 font-semi">Change subscription plan</p>
          <p class="text-sm m-b-sm">
            This is an immediate, admin-initiated billing switch for
            mid-contract changes. For subscribers already up for renewal, use
            the
            <a
              href="https://help.wrestlingiq.com/article/187-how-do-expirations-and-renewals-work-with-expiring-memberships"
              target="_blank"
              rel="noopener noreferrer"
              >renewal workflow</a
            >
            instead.
          </p>
          <ul class="text-sm m-b-sm p-l-m">
            <li>Next charge hits the normal bill date at the new rate.</li>
            <li>
              You choose whether the contract end date stays the same
              (same-length plans only) or restarts from that bill with the new
              plan's full length.
            </li>
            <li>
              <strong>No proration.</strong> Issue any refund for
              already-charged cycles manually.
            </li>
          </ul>
          <div v-if="availableExpiringTierPlans.length === 0">
            <p class="text-danger">
              No alternate plans are available for this subscription.
            </p>
          </div>
          <div v-else>
            <label>Which plan do you want to move this subscriber to?</label>
            <select v-model="tierToChangeTo" class="form-control m-b-m">
              <option :value="null" disabled>Select a plan...</option>
              <optgroup
                v-for="(plans, name) in tierPlansByProgram"
                :key="name"
                :label="name"
                v-if="plans.length > 0"
              >
                <option
                  v-for="plan in plans"
                  :value="plan.id"
                  :key="plan.id"
                  :disabled="plan.id === membershipBillingPlan.id"
                >
                  {{ plan.name }} — {{ tierLengthLabel(plan) }} ({{
                    formatAsCurrency(plan.amount)
                  }}/{{ plan.interval }})<span
                    v-if="plan.id === membershipBillingPlan.id"
                  >
                    (current plan)</span
                  >
                </option>
              </optgroup>
            </select>
            <div v-if="tierToChangeTo && selectedTierPlan">
              <label>What should happen to the contract end date?</label>
              <div class="radio" :class="{ disabled: !tierCanKeepEndDate }">
                <label :class="{ 'text-gray-500': !tierCanKeepEndDate }">
                  <input
                    type="radio"
                    value="keep"
                    v-model="tierEndDate"
                    :disabled="!tierCanKeepEndDate"
                  />
                  <strong>Keep the current end date</strong>
                  <span v-if="currentExpirationDate"
                    >({{ currentExpirationDate }})</span
                  >
                  <br />
                  <span v-if="tierCanKeepEndDate" class="text-12 text-gray-600"
                    >Best for moving between levels (e.g. beginner to advanced)
                    partway through a contract.</span
                  >
                  <span v-else class="text-12 text-gray-600"
                    >Only available when the new plan is also
                    {{ tierLengthLabel(membershipBillingPlan) }}.
                    {{ tierLengthLabel(selectedTierPlan) }} is a different
                    contract length, so start a new contract instead.</span
                  >
                </label>
              </div>
              <div class="radio">
                <label>
                  <input type="radio" value="restart" v-model="tierEndDate" />
                  <strong
                    >Start a new
                    {{ tierLengthLabel(selectedTierPlan) }} contract</strong
                  >
                  ({{ projectedExpirationDate }})
                  <br />
                  <span class="text-12 text-gray-600"
                    >Best for upgrading to a longer package (e.g. 3 months to 12
                    months).</span
                  >
                </label>
              </div>
              <p v-if="tierEndDate" class="text-12 text-gray-600 m-t-sm">
                Expires
                <span v-if="currentExpirationDate && tierEndDate === 'restart'"
                  >{{ currentExpirationDate }} → </span
                ><strong
                  :class="tierChangeShortensContract ? 'text-red-700' : ''"
                  >{{ tierChangeExpirationDate }}</strong
                >
                <span v-if="tierChangeShortensContract" class="text-red-700"
                  >(shortens contract)</span
                >
              </p>
            </div>
          </div>
          <div class="m-t-m">
            <button v-on:click="showChangeTier = false" class="btn btn-default">
              Cancel
            </button>
            <ladda-button
              @lbClicked="changeTier"
              el-class="btn-primary btn"
              :loading="changingTier"
            >
              Change plan
            </ladda-button>
          </div>
        </div>
        <p class="m-t-xs text-danger" v-html="errorMessage" v-if="error"></p>
      </div>
    </div>
    <pause-payments-modal
      v-if="showPauseConfirm"
      :subscription="subscription"
      :subtitle="pauseModalSubtitle"
      :allow-pause="allowPause"
      :allow-self-pause="allowSelfPause"
      :pause-options="pauseOptions"
      :policy-html="selfPausePolicyHtml"
      :once-per-year="selfPauseOncePerYear"
      :self-pause-locked-until="selfPauseLockedUntil"
      @close="showPauseConfirm = false"
    ></pause-payments-modal>
  </div>
</template>
<script>
import swal from 'sweetalert2';
import { billingPlanMix } from './mixins/billing_plan_mix';
import LaddaButton from '../../shared/ladda_button.vue';
import PausePaymentsModal from './pause_payments_modal.vue';
import Spinner from './spinner.vue';
import { errorableMix } from './mixins/errorable_mix';
import { formattingMixin } from './mixins/formatting_mix';

// Matches ManualInvoice::FEE_COVERAGE_DESCRIPTION, the donor's optional fee coverage line
const DONATION_FEE_COVERAGE = 'Contribution to cover card processing and WIQ fees';

export default {
  name: 'subscription-summary',
  components: {
    Spinner,
    LaddaButton,
    PausePaymentsModal,
  },
  mixins: [billingPlanMix, errorableMix, formattingMixin],
  props: {
    subscription: {
      type: Object,
      required: true,
    },
    allowCancel: {
      type: Boolean,
      default: true,
    },
    // Cancellation survey reason options ({ code, label }), sourced from
    // BillingSubscription::CANCELLATION_REASONS. Drives the dropdown in the cancel flow.
    cancellationReasons: {
      type: Array,
      default() {
        return [];
      },
    },
    allowPause: {
      type: Boolean,
      default: false,
    },
    // Members (non-coaches) self-pausing their own subscription. Constrained to pauseOptions,
    // with forced auto-resume (no arbitrary date / indefinite option — those are coach-only).
    allowSelfPause: {
      type: Boolean,
      default: false,
    },
    // Whole-billing-period pause choices resolved by the server against this subscription's bill
    // grid: [{ intervals, length, resumes_at }] (BillingSubscription#self_pause_options).
    // Radio presets in the pause modal for members and coaches alike. Empty when the subscription
    // can't be paused by period (yearly plans, already paused).
    pauseOptions: {
      type: Array,
      default() {
        return [];
      },
    },
    // Club pause rules (sanitized HTML) and the member's once-per-12-months lock, both passed
    // straight through to the pause modal. See pause_payments_modal.vue.
    selfPausePolicyHtml: {
      type: String,
      default: '',
    },
    selfPauseLockedUntil: {
      type: String,
      default: null,
    },
    // Team#self_pause_once_per_year: whether members are limited to one self pause per rolling
    // 12 months. Drives the "next pause" line and the modal's copy.
    selfPauseOncePerYear: {
      type: Boolean,
      default: true,
    },
    // Team#self_pause_allowed: whether members can self-pause at all. allowSelfPause is always
    // false for coaches, so this is what tells a coach's "next self pause" line the feature is off.
    selfPauseAllowed: {
      type: Boolean,
      default: false,
    },
    // The subscription's most recent pause, whoever started it (BillingSubscription#last_pause):
    // { paused_at, initiated_by_coach, initiator_name, resumed_at, resumes_at, still_paused }.
    // null when it has never been paused.
    lastPause: {
      type: Object,
      default: null,
    },
    allowCouponChange: {
      type: Boolean,
      default: false,
    },
    allowPlanChange: {
      type: Boolean,
      default: false,
    },
    // Admin-only change of an expiring subscription to another expiring plan — a different tier of
    // the same program or a different program entirely. Backed by available_expiring_plan_changes.
    allowExpiringTierChange: {
      type: Boolean,
      default: false,
    },
    availableExpiringTierPlans: {
      type: Array,
      default() {
        return [];
      },
    },
    allowDateChange: {
      type: Boolean,
      default: false,
    },
    canRenew: {
      type: Boolean,
      default: false,
    },
    renewalOpensAt: {
      type: String,
      default: null,
    },
    timesRenewed: {
      type: Number,
      default: 0,
    },
    contactEmail: {
      type: String,
      default: '',
    },
    // Why a canceled subscription ended, derived server-side (BillingSubscription#cancellation_cause):
    // 'expired' | 'member_or_admin' | 'payment_failure' | '' (blank when not canceled). Drives the
    // status badge nuance so support can tell an expired contract from an admin cancel from an unpaid cancel.
    cancellationCause: {
      type: String,
      default: '',
    },
    // The Legacy UI is for the old multi-wrestler possible in a subscription format.
    // In the new UI, we can assume that there is only 1 wrestler per subscription
    legacyUi: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      canceling: false,
      showPauseConfirm: false,
      showResumeConfirm: false,
      showChangeCoupon: false,
      showRemoveCoupon: false,
      showChangePlan: false,
      showChangeTier: false,
      showDateChange: false,
      couponToChangeTo: null,
      couponsLoading: false,
      changingCoupon: false,
      availableCoupons: [],
      availablePlans: [],
      planToChangeTo: null,
      loadingPlans: false,
      changingPlan: false,
      tierToChangeTo: null,
      // 'keep' | 'restart' — deliberately no default; the admin must pick one.
      tierEndDate: null,
      changingTier: false,
      showCancelScheduledPauseConfirm: false,
      cancelingScheduledPause: false,
      resuming: false,
      nextBillDate: null,
      changingBillDate: false,
    };
  },
  computed: {
    title() {
      if (this.legacyUi || !this.wrestlerProfile) {
        return `Subscription ${this.subscription.billing_partner_id}`;
      }

      return `${this.wrestlerProfile.full_name}`;
    },
    subtitle() {
      if (this.legacyUi) {
        return '';
      }

      if (this.membershipBillingPlan.product.paid_session_id) {
        return `<a href="/paid_sessions/${this.membershipBillingPlan.product.paid_session_id}">${this.membershipBillingPlan.product.name}</a>`;
      }

      return `${this.membershipBillingPlan.product.name}`;
    },
    endAtVerb() {
      switch (this.subscription.status) {
        case 'canceled':
          return 'Active until';
        case 'overdue':
          return 'Overdue since';
        default:
          return 'Next due';
      }
    },
    statusBadge() {
      switch (this.subscription.status) {
        case 'paid':
          return '<span class="label label-primary">Active</span>';
        case 'canceled': {
          // Prefer the server-derived cause; fall back to expires_on for older records where the
          // prop may be blank. A payment-failure cancel reads distinctly so support can spot it fast.
          if (this.cancellationCause === 'payment_failure') {
            return '<span class="label label-danger">Canceled · Unpaid</span>';
          }
          const expired = this.cancellationCause === 'expired'
            || (!this.cancellationCause && this.subscription.expires_on);
          const word = expired ? 'Expired' : 'Canceled';
          return `<span class="label">${word}</span>`;
        }
        case 'overdue':
          return '<span class="label label-warning">Overdue</span>';
        default:
          return '<span class="label">Unknown status</span>';
      }
    },
    formattedResumesAt() {
      if (this.subscription.payment_collection_resumes_at) {
        return moment(this.subscription.payment_collection_resumes_at).format(
          'M/D/YYYY',
        );
      }

      return '';
    },
    collectionBadge() {
      if (this.subscription.payment_collection_resumes_at) {
        return `<span class="label whitespace-normal bg-red-600 text-red-50"><i class="fa-solid fa-pause m-r-xs"></i>Payments paused until ${this.formattedResumesAt}</span>`;
      }
      return '<span class="label whitespace-normal bg-red-600 text-red-50"><i class="fa-solid fa-pause m-r-xs"></i>Payments paused</span>';
    },
    collectionPaused() {
      return this.subscription.payment_collection_status === 'paused';
    },
    pauseModalSubtitle() {
      if (this.legacyUi || !this.wrestlerProfile) return '';
      return `${this.wrestlerProfile.full_name}'s subscription`;
    },
    // "Sep 1, 2026 by you, resumed Oct 1, 2026" — the most recent pause, whoever started it.
    // Members read "by you" / "by Coach Name"; coaches read "by the member" / "by Coach Name".
    lastPauseSummary() {
      const pause = this.lastPause;
      if (!pause) return 'None yet';
      const fmt = (d) => moment(d).format('MMM D, YYYY');
      let who;
      if (pause.initiated_by_coach) {
        who = `by ${
          pause.initiator_name || (this.allowPause ? 'a coach' : 'your coach')
        }`;
      } else {
        who = this.allowPause
          ? `by ${pause.initiator_name || 'the subscriber'}`
          : 'by you';
      }
      let ending = 'no end date set';
      if (pause.resumed_at) {
        ending = `resumed ${fmt(pause.resumed_at)}`;
      } else if (pause.resumes_at) {
        ending = `resumes ${fmt(pause.resumes_at)}`;
      } else if (!pause.still_paused) {
        ending = 'ended';
      }
      return `${fmt(pause.paused_at)} ${who}, ${ending}`;
    },
    // When the member can self-pause again under the team's one-per-12-months rule (if it has
    // one). Coaches see the same line about the member; their own pauses are never limited.
    nextPauseSummary() {
      if (!this.selfPauseAllowed) {
        return 'Subscriber self pause is off for this team';
      }
      const subject = this.allowPause
        ? 'Subscriber can self-pause'
        : 'You can pause';
      if (this.subscription.status === 'overdue' && !this.collectionPaused) {
        return `${subject} once the overdue payment is paid`;
      }
      if (!this.selfPauseOncePerYear) {
        return this.allowPause
          ? 'No limit on subscriber self pauses'
          : 'No limit on how often you can pause';
      }
      if (this.selfPauseLockedUntil) {
        return `${subject} again after ${moment(
          this.selfPauseLockedUntil,
        ).format('MMM D, YYYY')} (one every 12 months)`;
      }
      if (this.collectionPaused || this.pauseScheduled) {
        return `${subject} again once the current pause ends (one every 12 months)`;
      }
      return `${subject} now (one every 12 months)`;
    },
    pauseScheduled() {
      return (
        !this.collectionPaused
        && !!this.subscription.payment_collection_pauses_at
      );
    },
    formattedPausesAt() {
      if (this.subscription.payment_collection_pauses_at) {
        return moment(this.subscription.payment_collection_pauses_at).format(
          'M/D/YYYY',
        );
      }

      return '';
    },
    scheduledPauseBadge() {
      if (this.subscription.payment_collection_resumes_at) {
        return `<span class="label whitespace-normal bg-yellow-600 text-yellow-50"><i class="fa-regular fa-clock m-r-xs"></i>Payments pause on ${this.formattedPausesAt} until ${this.formattedResumesAt}</span>`;
      }
      return `<span class="label whitespace-normal bg-yellow-600 text-yellow-50"><i class="fa-regular fa-clock m-r-xs"></i>Payments pause on ${this.formattedPausesAt}</span>`;
    },
    // New UI shortcuts that work based on billing conventions of 1 wrestler per membership
    wrestlerProfile() {
      return _.head(
        _.uniqBy(
          _.flatMap(
            this.subscription.billing_plan_subscription_items,
            'wrestler_profiles',
          ),
          'id',
        ),
      );
    },
    membershipBillingPlan() {
      return _.filter(
        this.subscription.billing_plans,
        (p) => !p.is_wiq_fee && !p.is_tax_item && p.name !== DONATION_FEE_COVERAGE,
      )[0];
    },
    isEmptyPlans() {
      return _.isEmpty(this.availablePlans);
    },
    isDonation() {
      return this.membershipBillingPlan?.product?.name === 'Donation';
    },
    hasCoupon() {
      return this.subscription.coupons.length > 0;
    },
    renewalOpensAtLabel() {
      return moment(this.renewalOpensAt).format('MMM D, YYYY');
    },
    renewalUrl() {
      return `/subscriptions/${this.subscription.billing_partner_id}/renew`;
    },
    // Eligible expiring plans grouped by program (product) for the change-plan picker, mirroring
    // the non-expiring loadAvailablePlans() grouping. The current plan's program holds the tier
    // alternates; other programs are cross-program upgrades/downgrades.
    tierPlansByProgram() {
      return _.groupBy(
        this.availableExpiringTierPlans,
        (p) => p.product_name || 'Other',
      );
    },
    selectedTierPlan() {
      if (!this.tierToChangeTo) {
        return null;
      }
      return _.find(this.availableExpiringTierPlans, {
        id: this.tierToChangeTo,
      });
    },
    projectedExpirationDate() {
      const plan = this.selectedTierPlan;
      if (!plan || !this.subscription.current_period_end_at) {
        return '';
      }
      const cycles = plan.intervals_to_bill * plan.interval_count;
      const unitMap = { month: 'months', year: 'years', week: 'weeks' };
      const unit = unitMap[plan.interval] || 'months';
      return moment(this.subscription.current_period_end_at)
        .add(cycles, unit)
        .subtract(1, 'days')
        .format('MMM D, YYYY');
    },
    currentExpirationDate() {
      if (!this.subscription.expires_on) {
        return '';
      }
      return moment(this.subscription.expires_on).format('MMM D, YYYY');
    },
    projectedDateShortensContract() {
      const plan = this.selectedTierPlan;
      if (
        !plan
        || !this.subscription.expires_on
        || !this.subscription.current_period_end_at
      ) {
        return false;
      }
      const cycles = plan.intervals_to_bill * plan.interval_count;
      const unitMap = { month: 'months', year: 'years', week: 'weeks' };
      const unit = unitMap[plan.interval] || 'months';
      const projected = moment(this.subscription.current_period_end_at)
        .add(cycles, unit)
        .subtract(1, 'days');
      return projected.isBefore(moment(this.subscription.expires_on));
    },
    // Keeping the end date is only allowed between same-length contracts (the model enforces this too)
    tierCanKeepEndDate() {
      const current = this.membershipBillingPlan;
      const plan = this.selectedTierPlan;
      if (!current || !plan) {
        return false;
      }
      return (
        current.interval === plan.interval
        && current.intervals_to_bill * current.interval_count
          === plan.intervals_to_bill * plan.interval_count
      );
    },
    tierChangeExpirationDate() {
      return this.tierEndDate === 'keep'
        ? this.currentExpirationDate
        : this.projectedExpirationDate;
    },
    tierChangeShortensContract() {
      return (
        this.tierEndDate === 'restart' && this.projectedDateShortensContract
      );
    },
  },
  watch: {
    tierToChangeTo() {
      if (this.tierEndDate === 'keep' && !this.tierCanKeepEndDate) {
        this.tierEndDate = null;
      }
    },
    showChangeCoupon() {
      if (this.availableCoupons.length === 0) {
        this.loadCoupons();
      }
    },
    showChangePlan() {
      if (this.availablePlans.length === 0) {
        this.loadAvailablePlans();
      }
    },
  },
  mounted() {
    // The receipt "pause membership" magic link lands here with ?intent=pause. Auto-open the
    // self-pause modal so the member can pick a duration without hunting for the button.
    const params = new URLSearchParams(window.location.search);
    if (
      params.get('intent') === 'pause'
      && (this.allowSelfPause || this.allowPause)
      && !this.collectionPaused
    ) {
      this.showPauseConfirm = true;
    }
  },
  methods: {
    loadAvailablePlans() {
      // We only allow changing to non expiring plans that have the same interval and interval count
      const ransack = {
        q: {
          interval_eq: this.subscription.interval,
          interval_count_eq: this.subscription.interval_count,
          intervals_to_bill_null: '1',
          active_true: '1',
        },
      };
      const url = this.$apiService.billingPlans(ransack);
      const vm = this;
      vm.loadingPlans = true;
      vm.$apiService
        .loadAllPages(url, 'billing_plans')
        .then((objects) => {
          // Donors can only move between donation tiers (the active ones on the public donation page),
          // and members can never be moved onto a donation tier
          vm.availablePlans = _.groupBy(
            _.filter(
              objects,
              (p) => p.is_wiq_fee === false
                && p.is_tax_item === false
                && p.name !== DONATION_FEE_COVERAGE
                && (p.product.name === 'Donation') === vm.isDonation,
            ),
            'product.name',
          );
          vm.loadingPlans = false;
        })
        .catch((error) => {
          console.error(error);
          vm.errorMessage = 'Something went wrong loading available plans. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.loadingPlans = false;
        });
    },
    loadCoupons() {
      const vm = this;
      const url = vm.$apiService.couponsUrl();
      vm.couponsLoading = true;
      vm.$apiService
        .loadAllPages(url, 'coupons')
        .then((objects) => {
          vm.availableCoupons = objects;
          vm.couponsLoading = false;
        })
        .catch((error) => {
          console.error(error);
          vm.errorMessage = 'Something went wrong loading available scholarship codes. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.couponsLoading = false;
        });
    },
    confirmCancel() {
      const vm = this;

      if (this.allowCancel) {
        const optionsHtml = vm.cancellationReasons
          .map((r) => `<option value="${r.code}">${r.label}</option>`)
          .join('');

        swal
          .fire({
            icon: 'warning',
            title: 'Cancel this subscription?',
            html: `
              <div class="text-left m-t-sm">
                <label for="cancellation-reason-code" class="text-12 text-gray-600 m-b-xxs">Can you tell us why you're canceling? <span class="text-danger">*</span></label>
                <select id="cancellation-reason-code" class="form-control">
                  <option value="">Select a reason…</option>
                  ${optionsHtml}
                </select>
                <label for="cancellation-reason-text" class="text-12 text-gray-600 m-t-sm m-b-xxs">Anything else? <span class="text-gray-400">(optional)</span></label>
                <textarea id="cancellation-reason-text" class="form-control" rows="3" placeholder="Required if you chose “Other.”"></textarea>
              </div>
          `,
            // Focus the safe "No, keep subscription" button instead of
            // letting SweetAlert fall through to focusing the first focusable
            // element (the reason <select>). On iOS, programmatically focusing
            // a native select pops the picker open as soon as the modal shows.
            focusConfirm: false,
            focusCancel: true,
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: 'Yes, cancel subscription',
            cancelButtonText: 'No, keep subscription',
            buttonsStyling: false,
            customClass: {
              popup: 'swal-cancellation-survey',
              confirmButton: 'btn btn-danger m-l-md',
              cancelButton: 'btn btn-default',
            },
            preConfirm: () => {
              const code = document.getElementById(
                'cancellation-reason-code',
              ).value;
              const text = document
                .getElementById('cancellation-reason-text')
                .value.trim();
              if (!code) {
                swal.showValidationMessage(
                  'Please select a reason for canceling.',
                );
                return false;
              }
              if (code === 'other' && !text) {
                swal.showValidationMessage(
                  'Please add a few details when choosing Other.',
                );
                return false;
              }
              return { code, text };
            },
          })
          .then((result) => {
            if (result.isConfirmed && result.value) {
              vm.cancelSub(result.value);
            }
          });
      } else {
        swal
          .fire({
            icon: 'info',
            title: `Please contact ${this.contactEmail}`,
            text: `To cancel your subscription please contact your coach or email ${this.contactEmail} directly.`,
            confirmButtonText: 'Ok',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn btn-default',
            },
          })
          .then((result) => {});
      }
    },
    cancelSub: _.throttle(function (reason = null) {
      const vm = this;
      vm.error = false;
      vm.canceling = true;

      const deleteUrl = vm.$apiService.subscriptionUrl(this.subscription.id);
      const config = reason
        ? {
          data: {
            cancellation_reason_code: reason.code,
            cancellation_reason_text: reason.text,
          },
        }
        : undefined;
      axios
        .delete(deleteUrl, config)
        .then((response) => {
          vm.$notificationManager.emit(
            'show-toast',
            'Subscription canceled',
            true,
          );
          vm.canceling = false;
          window.location.reload();
        })
        .catch((error) => {
          vm.errorMessage = 'Something went wrong canceling the subscription. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.canceling = false;
        });
    }, 500),
    removeCoupon() {
      const vm = this;
      vm.error = false;
      vm.changingCoupon = true;

      const deleteCouponUrl = vm.$apiService.subscriptionDeleteCouponUrl(
        this.subscription.id,
      );
      axios
        .delete(deleteCouponUrl)
        .then((response) => {
          vm.$notificationManager.emit('show-toast', 'Discount removed', true);
          vm.changingCoupon = false;
          window.location.reload();
        })
        .catch((error) => {
          vm.errorMessage = 'Something went wrong removing the discount. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.changingCoupon = false;
        });
    },
    changeCoupon: _.throttle(function () {
      const vm = this;
      if (vm.changingCoupon) {
        return;
      }

      if (vm.couponToChangeTo == null) {
        vm.errorMessage = 'Please select a scholarship code.';
        vm.error = true;
        return;
      }

      vm.error = false;
      vm.changingCoupon = true;

      const changeCouponUrl = vm.$apiService.subscriptionCouponUrl(
        this.subscription.id,
      );
      const couponParams = {
        billing_subscription: {
          coupon: {
            id: this.couponToChangeTo,
          },
        },
      };
      axios
        .post(changeCouponUrl, couponParams)
        .then((response) => {
          vm.$notificationManager.emit(
            'show-toast',
            'Scholarship code applied',
            true,
          );
          vm.changingCoupon = false;
          window.location.reload();
        })
        .catch((error) => {
          vm.errorMessage = 'Something went wrong changing the scholarship code. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.changingCoupon = false;
        });
    }, 500),
    openChangeTier() {
      this.tierToChangeTo = null;
      this.tierEndDate = null;
      this.showChangeTier = true;
    },
    tierLengthLabel(plan) {
      if (!plan) {
        return '';
      }
      const cycles = plan.intervals_to_bill * plan.interval_count;
      const unit = cycles > 1 ? `${plan.interval}s` : plan.interval;
      return `${cycles} ${unit}`;
    },
    changeTier() {
      const vm = this;

      if (vm.tierToChangeTo == null) {
        vm.errorMessage = 'Please select a plan to change to.';
        vm.error = true;
        return;
      }

      if (vm.tierToChangeTo === vm.membershipBillingPlan.id) {
        vm.errorMessage = 'Pick a plan different than the current one.';
        vm.error = true;
        return;
      }

      if (vm.tierEndDate == null) {
        vm.errorMessage = 'Please choose whether to keep the current end date or start a new contract.';
        vm.error = true;
        return;
      }

      const newPlan = vm.selectedTierPlan;
      const willShorten = vm.tierChangeShortensContract;
      const endDateLine = vm.tierEndDate === 'keep'
        ? `<p>Contract end date stays <strong>${vm.currentExpirationDate}</strong></p>`
        : `<p>New expiration date: <strong>${vm.projectedExpirationDate}</strong></p>`;
      const shortenLine = willShorten
        ? '<p style="color: #b91c1c;"><strong>Heads up: this will shorten the contract.</strong></p>'
        : '';

      swal
        .fire({
          icon: willShorten ? 'warning' : 'question',
          titleText: `Change plan to ${newPlan.name}?`,
          html: `
          ${shortenLine}
          ${endDateLine}
          <p class="text-sm">No proration — issue any refunds for already-charged cycles manually.</p>
        `,
          showCancelButton: true,
          reverseButtons: true,
          confirmButtonText: 'Yes, change plan',
          cancelButtonText: 'Cancel',
          buttonsStyling: false,
          customClass: {
            confirmButton: willShorten
              ? 'btn btn-warning m-l-md'
              : 'btn btn-primary m-l-md',
            cancelButton: 'btn btn-default',
          },
        })
        .then((result) => {
          if (result.value) {
            vm.doChangeTier();
          }
        });
    },
    doChangeTier: _.throttle(function () {
      const vm = this;
      if (vm.changingTier) {
        return;
      }

      vm.error = false;
      vm.changingTier = true;

      const url = vm.$apiService.subscriptionChangeExpiringPlanUrl(
        vm.subscription.id,
      );
      const params = {
        billing_subscription: {
          change_expiring_plan: {
            plan_id: vm.tierToChangeTo,
            keep_expiration: vm.tierEndDate === 'keep',
          },
        },
      };
      axios
        .post(url, params)
        .then(() => {
          vm.$notificationManager.emit(
            'show-toast',
            'Subscription plan changed',
            true,
          );
          vm.changingTier = false;
          window.location.reload();
        })
        .catch((error) => {
          vm.errorMessage = vm.parseErrorResponse(error)
            || 'Something went wrong changing the plan. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.changingTier = false;
        });
    }, 500),
    changePlan: _.throttle(function () {
      const vm = this;

      if (vm.changingPlan || this.legacyUi) {
        return;
      }

      if (vm.planToChangeTo == null) {
        vm.errorMessage = 'Please select a plan to change to.';
        vm.error = true;
        return;
      }

      vm.error = false;
      vm.changingPlan = true;

      const changePlan = vm.$apiService.subscriptionChangePlanUrl(
        this.subscription.id,
      );
      const planParams = {
        billing_subscription: {
          replace_plan: {
            plan_to_delete: this.membershipBillingPlan.id,
            plan_to_add: this.planToChangeTo,
          },
        },
      };
      axios
        .post(changePlan, planParams)
        .then((response) => {
          vm.$notificationManager.emit(
            'show-toast',
            'Billing plan changed',
            true,
          );
          vm.changingPlan = false;
          window.location.reload();
        })
        .catch((error) => {
          vm.errorMessage = vm.parseErrorResponse(error)
            || `Something went wrong changing billing plans. Try again or email support@wrestlingiq.com ${error.toString()}`;
          vm.error = true;
          vm.changingPlan = false;
        });
    }, 500),
    showPausePopup() {
      swal
        .fire({
          icon: 'info',
          title: `Please contact ${this.contactEmail}`,
          text: `To pause or resume payments contact your coach or email ${this.contactEmail} directly.`,
          confirmButtonText: 'Ok',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn btn-default',
          },
        })
        .then((result) => {});
    },
    cancelScheduledPause: _.throttle(function () {
      const vm = this;
      vm.error = false;
      vm.cancelingScheduledPause = true;

      const pauseUrl = vm.$apiService.subscriptionPausePaymentsUrl(
        this.subscription.id,
      );
      axios
        .delete(pauseUrl)
        .then((response) => {
          vm.$notificationManager.emit(
            'show-toast',
            'Scheduled pause canceled',
            true,
          );
          vm.cancelingScheduledPause = false;
          window.location.reload();
        })
        .catch((error) => {
          vm.errorMessage = 'Something went wrong canceling the scheduled pause. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.cancelingScheduledPause = false;
        });
    }, 500),
    resumePayments() {
      const vm = this;
      vm.error = false;
      vm.resuming = true;

      const resumeUrl = vm.$apiService.subscriptionResumePaymentsUrl(
        this.subscription.id,
      );
      axios
        .post(resumeUrl, {})
        .then((response) => {
          vm.$notificationManager.emit(
            'show-toast',
            'Payment collection resumed',
            true,
          );
          vm.resuming = false;
          window.location.reload();
        })
        .catch((error) => {
          vm.errorMessage = 'Something went wrong resuming payments. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.resuming = false;
        });
    },
    changeBillDate: _.throttle(function () {
      const vm = this;
      vm.error = false;
      if (vm.changingBillDate || !vm.nextBillDate) {
        return;
      }
      vm.changingBillDate = true;

      // Send the raw datetime-local string (no offset) so the server anchors
      // it to the team's time zone — not the admin's browser, which used to
      // shift bill dates for admins traveling outside the team's zone
      const params = {
        billing_subscription: {
          current_period_end_at: vm.nextBillDate,
        },
      };
      const updateUrl = vm.$apiService.subscriptionUrl(this.subscription.id);
      axios
        .put(updateUrl, params)
        .then((response) => {
          vm.$notificationManager.emit('show-toast', 'Bill date changed', true);
          vm.changingBillDate = false;
          window.location.reload();
        })
        .catch((error) => {
          vm.errorMessage = this.parseErrorResponse(error);
          vm.error = true;
          vm.changingBillDate = false;
        });
    }, 500),
  },
};
</script>
